import type { Filter } from 'contexts/preferences/listing-params/types';
import { getNSEWWithBoundaryString } from 'utils/boundary';
import { MapBoundingBox } from './value-objects/map-bounding-box';
import { SavedSearchPolicy } from './policies/saved-search-policy';
import type { MapBounds, SavedSearchFilterParams } from './types';

/**
 * Returns the filter located by the map's rectangle rather than by an area
 * slug, dropping any slug it was carrying.
 *
 * On a search page the map is the search. A slug can still be sitting in the
 * filter, because `listingParams` is context that survives soft navigation, so
 * arriving from an area page leaves the previous area behind. Only one code
 * path on the search page clears it, the one that reads the filter from the
 * URL.
 *
 * Leaving it there does not merely add noise, it decides the search.
 * client-app's `SavedSearchFilter#geo` returns `{ area_slug: }` and never looks
 * at `bbox` or `boundary` when a slug is present, so a search saved from a
 * street-level viewport in Toronto silently covers all of Toronto. The count,
 * the card image and every alert email follow the slug.
 *
 * `boundary` is cleared alongside the slug when a box is available, for the
 * same reason one step down: client-app prefers `boundary` over `bbox`, and a
 * boundary inherited from an area page would win over the viewport we just
 * measured.
 *
 * The slug is dropped whether or not there is anything to replace it with. A
 * search saved from this page is defined by the map, so a slug on it is always
 * wrong, and leaving one in as a fallback would quietly save the wrong area.
 * With no geometry either, the search has no location and
 * `SavedSearchPolicy.hasGeography` rejects it before the request is made.
 */
export function asMapBoundedSearch(
  filter: Filter,
  bounds?: MapBounds | null
): SavedSearchFilterParams {
  const boundingBox = MapBoundingBox.fromBounds(bounds);
  if (boundingBox) {
    return { ...filter, slug: '', boundary: null, bbox: boundingBox.toParam() };
  }

  // No rectangle to record. The legacy search page saves the viewport as a
  // `boundary` polygon instead, so whatever it has is left in place; if that is
  // empty too, the search has no location and validation catches it.
  return { ...filter, slug: '' };
}

/**
 * The map area a saved search covers. Null if the search has no map area.
 *
 * To apply a saved search, the map has to be moved to the right place. A search
 * saved from a map stores its area in one of two fields. Search page v2 stores
 * a `bbox` (see {@link asMapBoundedSearch}). The legacy search page stores a
 * `boundary` polygon. A search saved for a named area stores neither, because
 * its slug identifies the area. That case returns null.
 *
 * If a filter has both fields, this reads the `boundary`. client-app also reads
 * the `boundary` and ignores the `bbox`. So the `boundary` is the area that
 * client-app counted the listings over. Using the `bbox` instead could show a
 * different number of listings.
 */
export function savedSearchExtent(filter: Filter): MapBounds | null {
  if (SavedSearchPolicy.hasUsableBoundary(filter)) {
    try {
      const extent = getNSEWWithBoundaryString(String(filter.boundary));
      const corners = [extent.north, extent.south, extent.east, extent.west];
      if (corners.every(Number.isFinite)) {
        return extent;
      }
    } catch {
      // Not parseable as a polygon, so it says nothing about where to go.
    }
  }

  const boundingBox = MapBoundingBox.fromParam((filter as SavedSearchFilterParams).bbox);
  if (!boundingBox) {
    return null;
  }
  return {
    north: boundingBox.north,
    south: boundingBox.south,
    east: boundingBox.east,
    west: boundingBox.west,
  };
}

/**
 * True if the filter's `zoom` value is the zoom the search was saved at.
 *
 * The legacy search page updates `zoom` every time the map moves. It also
 * saves the map area as a `boundary`. So if a filter has a `boundary`, its
 * `zoom` value is correct.
 *
 * Search page v2 did not update `zoom` until `persistViewport` was added to
 * `use-search-page-v2-workspace`. Any search saved on that page before then has
 * whatever `zoom` value was already in the cookie, from an earlier page. That
 * value can be wrong. For example it can be 12, for a city, when the saved
 * `bbox` covers one street. Using it would show the wrong number of listings.
 *
 * A search saved on that page now does have a correct `zoom`, but there is no
 * way to tell the two apart. So if a filter has a `bbox`, callers should
 * calculate the zoom from the `bbox` and ignore the `zoom` value. For a search
 * saved since `persistViewport`, the two agree anyway, because the `bbox` and
 * the `zoom` are written from the same map view.
 */
export function recordsItsOwnZoom(filter: Filter): boolean {
  const bbox = (filter as SavedSearchFilterParams).bbox?.trim();
  return !bbox && !!filter.zoom;
}
